import React, {useCallback, useMemo, useState} from "react";
import Api from "../../../../../../../core/services/api/api";
import {deepEqual, formatPhoneNumber} from "../../../../../../../core/services/utils/utils";
import useIsMounted from "../../../../../../hooks/use-is-mounted";
import Form from "../../../../../../components/base/form";
import {OutlinedDropdownForm} from "../../../../../../components/base/dropdown/outlined_dropdown";
import {OutlinedInputForm} from "../../../../../../components/base/input/outlined_input";
import Button from "../../../../../../components/base/button/button";
import ValidateMessages from "../../../../../../../core/models/static/texts/vallidate-messages";
import PhoneInput from "../../../../../../components/base/input/phone-input";
import {MuiRFF, Yup} from '../../../../../../components/form';
import {ReduxActions, useAppDispatch} from "../../../../../../redux";
import {Box, Paper, Typography} from "@mui/material";
import EnvService from "../../../../../../../core/services/env";
import Grid2 from "@mui/material/Unstable_Grid2";
import CopyIconButton from "../../../../../../components/base/copy-to-clipboard/copy-icon-button";

const schema = Yup.object().shape({
    name: Yup.string().trim().nullable().required(ValidateMessages.required),
    email: Yup.string().trim().nullable().email(ValidateMessages.incorrectType("email")).required(ValidateMessages.required),
    country: Yup.mixed().nullable().required(ValidateMessages.required),
    province: Yup.mixed().nullable().required(ValidateMessages.required),
    phone: Yup.string().trim().nullable().required(ValidateMessages.required).phoneNo(ValidateMessages.incorrectType("phone"), 10),
    city: Yup.string().trim().nullable().required(ValidateMessages.required),
    postalCode: Yup.string().trim().nullable().required(ValidateMessages.required),
    address: Yup.string().trim().nullable().required(ValidateMessages.required),
});

const validate = MuiRFF.makeValidate(schema);
const required = MuiRFF.makeRequired(schema);

const CustomerProfileInformationSection = ({customer, setCustomer, formData, deleting}) => {
    const [updating, setUpdating] = useState(false);
    const isMounted = useIsMounted();
    const dispatch = useAppDispatch();

    const initialValues = useMemo(() => ({
        name: customer?.name,
        email: customer?.email,
        country: formData.countries?.find(e => e.id === customer?.countryId),
        province: formData.provinces?.find(e => e.id === customer?.provinceId),
        city: customer?.city,
        phone: formatPhoneNumber(customer?.phone),
        postalCode: customer?.postalCode,
        address: customer?.address,
    }), [customer?.address, customer?.city, customer?.countryId, customer?.email, customer?.name, customer?.phone, customer?.postalCode, customer?.provinceId, formData.countries, formData.provinces]);

    /**
     * Calls the api for updating the information of the company and if successful, updates the internal state with
     * the new information.
     * @param values {any} the form values used for update.
     */
    const updateCompanyInfo = useCallback(async (values) => {
        setUpdating(true);
        const response = await Api.updateCustomerInfo({
            id: customer.id,
            creditLimit: customer.creditLimit,
            ...values
        });
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            dispatch(ReduxActions.customerInformation.setCustomerInformation({name: values.name,}));
            setCustomer(prevState => ({
                ...prevState,
                name: values.name,
                countryId: values.country.id,
                country: values.country,
                provinceId: values.province.id,
                province: values.province,
                city: values.city,
                phone: values.phone,
                postalCode: values.postalCode,
                address: values.address,
                email: values.email,
            }));
        }
        setUpdating(false);
    }, [dispatch, isMounted, setCustomer, customer])

    return (
        <>
            <Paper sx={{padding: 2, display: 'flex', flexDirection: 'column', height: '100%'}}>
                <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBlockEnd: 3}}>
                    <Typography variant={'subtitle1'} fontWeight={600}>
                        Customer Information
                    </Typography>
                    <Box sx={{
                        paddingInline: 2,
                        paddingBlock: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        backgroundColor: 'background.tertiary',
                        borderRadius: 3,
                    }}>
                        <Typography variant={'body1'} fontWeight={600} color={'tertiary.main'} sx={{display: 'inline-flex', alignItems: 'center'}}>
                            <Typography
                                variant={'body2'}
                                component={'span'}
                                color={'text.secondary'}
                                fontWeight={400}
                                marginInlineEnd={0.5}
                            >
                                Code:
                            </Typography> {customer?.code ?? ""}
                            <CopyIconButton value={customer?.code}/>
                        </Typography>
                    </Box>
                </Box>
                <Form
                    onSubmit={updateCompanyInfo}
                    validate={validate}
                    initialValues={initialValues}
                    render={({submitting, values, initialValues, form}) => {
                        return (
                            <Grid2 container spacing={2}>
                                <Grid2 xs={12} md={6} lg={12} xl={6}>
                                    <OutlinedInputForm
                                        label={'Name'}
                                        name={`name`}
                                        required={required.name}
                                        value={values.name ?? ''}
                                        onChange={(e) => form.change(e.target.name, e.target.value)}
                                        disabled={updating || deleting}
                                    />
                                </Grid2>
                                <Grid2 xs={12} md={6} lg={12} xl={6}>
                                    <PhoneInput
                                        form={form}
                                        label={'Phone'}
                                        aria-label={'Phone of the user\'s company'}
                                        name={'phone'}
                                        required={required.phone}
                                        value={values?.phone ?? ''}
                                        disabled={updating || deleting}
                                    />
                                </Grid2>
                                <Grid2 xs={12}>
                                    <OutlinedInputForm
                                        label={'Email'}
                                        name={'email'}
                                        type={'text'}
                                        required={required.email}
                                        value={values?.email ?? ''}
                                        onChange={(e) => form.change(e.target.name, e.target.value)}
                                        disabled={updating || deleting}
                                    />
                                </Grid2>
                                <Grid2 xs={12} md={6} lg={12} xl={6}>
                                    <OutlinedInputForm
                                        label={'Address'}
                                        name={'address'}
                                        type={'text'}
                                        required={required.address}
                                        value={values?.address ?? ''}
                                        onChange={(e) => form.change(e.target.name, e.target.value)}
                                        disabled={updating || deleting}
                                    />
                                </Grid2>
                                <Grid2 xs={12} md={6} lg={12} xl={6}>
                                    <OutlinedInputForm
                                        label={'City'}
                                        name={'city'}
                                        type={'text'}
                                        required={required.city}
                                        value={values?.city ?? ''}
                                        onChange={(e) => form.change(e.target.name, e.target.value)}
                                        disabled={updating || deleting}
                                    />
                                </Grid2>
                                <Grid2 xs={12} md={6} lg={12} xl={6}>
                                    <OutlinedDropdownForm
                                        label={!values.country ? 'Province / State' : values.country?.name === EnvService.USATitle ? "State" : 'Province'}
                                        name={`province`}
                                        required={required.province}
                                        data={formData.provinces?.filter(e => e.countryId === values.country?.id)?.map(e => ({
                                            value: e,
                                            label: e.name,
                                            id: e.id
                                        })) ?? []}
                                        disabled={updating || deleting}
                                    />
                                </Grid2>
                                <Grid2 xs={12} md={6} lg={12} xl={6}>
                                    <OutlinedDropdownForm
                                        label={'Country'}
                                        onChange={() => form.change('province', null)}
                                        name={`country`}
                                        required={required.country}
                                        data={formData.countries.map(e => ({
                                            value: e,
                                            label: e.name,
                                            id: e.id
                                        })) ?? []}
                                        disabled={updating || deleting}
                                    />
                                </Grid2>
                                <Grid2 xs={12} md={6} lg={12} xl={6}>
                                    <OutlinedInputForm
                                        label={!values.country ? 'Postal/Zip Code' : values.country?.name === EnvService.USATitle ? "Zip Code" : 'Postal Code'}
                                        name={'postalCode'}
                                        type={'text'}
                                        required={required.postalCode}
                                        value={values?.postalCode ?? ''}
                                        onChange={(e) => form.change(e.target.name, e.target.value)}
                                        disabled={updating || deleting}
                                    />
                                </Grid2>
                                <Grid2 xs={12} md={6} lg={12} xl={6} className={`d-flex justify-content-center`}>
                                    <Button
                                        disabled={!!submitting || deepEqual(initialValues, values) || updating || deleting}
                                        type={'submit'}
                                        variant={'contained'}
                                        color={'primary'}
                                        className='w-100'
                                        size={'large'}
                                    >
                                        {
                                            updating ? 'Updating...' : "Update"
                                        }
                                    </Button>
                                </Grid2>
                            </Grid2>
                        );
                    }}
                />
            </Paper>
        </>
    );
}

export default CustomerProfileInformationSection;
